import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CompetitionResultEntryTable from '../../../components/tables/CompetitionResultEntryTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCompetitionResultEntrySearch, useDeleteCompetitionResultEntry } from '../../../hooks/useCompetitionResultEntryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CompetitionResultEntryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const CompetitionResultEntryList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CompetitionResultEntryDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useCompetitionResultEntrySearch();
  const deleteCompetitionResultEntry = useDeleteCompetitionResultEntry();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competition Result Entries', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Competition Result Entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: CompetitionResultEntryDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCompetitionResultEntry.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting CompetitionResultEntry:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/competitionresultentries"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Manage.CompetitionResultEntry.View'
    >
      <Card
        title="Manage Competition Result Entries"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/competitionresultentries/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <FormField
                            id="competitionResultId"
                            label="Search by Competition Result Id"
                            value={formFilters.competitionResultId || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, competitionResultId: e.target.value })}
                            placeholder="Search by Competition Result Id"
                        />
                        <FormField
                            id="competitionExerciseId"
                            label="Search by Competition Exercise Id"
                            value={formFilters.competitionExerciseId || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, competitionExerciseId: e.target.value })}
                            placeholder="Search by Competition Exercise Id"
                        />
                        <FormField
                            id="scoreId"
                            label="Search by Score Id"
                            value={formFilters.scoreId || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, scoreId: e.target.value })}
                            placeholder="Search by Score Id"
                        />
                        <FormField
                            id="position"
                            label="Search by Position"
                            value={formFilters.position || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, position: e.target.value })}
                            placeholder="Search by Position"
                        />
                        <FormField
                            id="rank"
                            label="Search by Rank"
                            value={formFilters.rank || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, rank: e.target.value })}
                            placeholder="Search by Rank"
                        />

          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CompetitionResultEntryTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/competitionresultentries/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionResultEntryList;
